import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import { navItems, site } from "../data/site";
import { useColorMode } from "../theme/ColorModeProvider";

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleMode } = useColorMode();
  const { pathname } = useLocation();

  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          backgroundColor: scrolled
            ? theme.palette.mode === "dark"
              ? "rgba(10, 10, 10, 0.72)"
              : "rgba(255, 255, 255, 0.72)"
            : "transparent",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          borderBottom: scrolled ? theme.tokens.hairline : "1px solid transparent",
          transition: theme.transitions.create(
            ["background-color", "border-color", "backdrop-filter"],
            { duration: 250 }
          ),
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: 1 }}>
            <Typography
              component={NavLink}
              to="/"
              variant="h6"
              sx={{
                flexGrow: 1,
                fontSize: { xs: "1.1rem", md: "1.35rem" },
                fontWeight: 600,
                color: "text.primary",
                textDecoration: "none",
              }}
            >
              {site.name}
            </Typography>

            {!isMobile && (
              <Box
                component="nav"
                aria-label="Main"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {navItems.map(({ label, path }) => {
                  const active = pathname === path;

                  return (
                    <Box
                      key={path}
                      component={NavLink}
                      to={path}
                      aria-current={active ? "page" : undefined}
                      sx={{
                        position: "relative",
                        px: 1.5,
                        py: 1,
                        fontSize: "0.95rem",
                        color: "text.primary",
                        textDecoration: "none",
                        opacity: active ? 1 : 0.7,
                        transition: "opacity 0.2s ease",
                        "&:hover": { opacity: 1 },
                        // Underline that grows from the centre on hover/active.
                        // Scaled rather than width-animated so it composites
                        // instead of triggering layout on every frame.
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: "20%",
                          bottom: 6,
                          height: "2px",
                          width: "60%",
                          backgroundColor: "text.primary",
                          transform: `scaleX(${active ? 1 : 0})`,
                          transformOrigin: "center",
                          transition: "transform 0.25s ease",
                        },
                        "&:hover::after": { transform: "scaleX(1)" },
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            )}

            <Tooltip
              title={mode === "dark" ? "Switch to light" : "Switch to dark"}
            >
              <IconButton
                onClick={toggleMode}
                color="inherit"
                aria-label={
                  mode === "dark" ? "Switch to light mode" : "Switch to dark mode"
                }
                sx={{ width: 40, height: 40 }}
              >
                {mode === "dark" ? (
                  <DarkModeIcon sx={{ fontSize: 20 }} />
                ) : (
                  <LightModeIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>

            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="Open navigation menu"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{ paper: { sx: { width: 260 } } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={closeDrawer} aria-label="Close navigation menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List component="nav" aria-label="Main">
          {navItems.map(({ label, path }) => (
            <ListItemButton
              key={path}
              component={NavLink}
              to={path}
              onClick={closeDrawer}
              selected={pathname === path}
              aria-current={pathname === path ? "page" : undefined}
            >
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
